"use client";

import { useRef } from "react";
import viugoLiveWifi16 from "@/assets/images/viugo/kbv-v16-live-wifi-16.svg";
import viugoLiveStatH from "@/assets/images/viugo/kbv-v16-live-stat-h-score.svg";
import viugoLiveCornerMobile from "@/assets/images/viugo/kbv-v16-live-stat-corner-mobile.svg";
import viugoLiveCornerDesktop from "@/assets/images/viugo/kbv-v16-live-stat-corner-desktop.svg";
import viugoLiveCardYellow from "@/assets/images/viugo/kbv-v16-live-stat-card-yellow.svg";
import viugoLiveCardRed from "@/assets/images/viugo/kbv-v16-live-stat-card-red.svg";
import ViuGoOddsMatchTime from "./ViuGoOddsMatchTime";
import ViuGoOddsColumn from "./ViuGoOddsColumn";
import ViuGoMobileOddsSlide from "./ViuGoMobileOddsSlide";

const LIVE_INFO_ITEM_MOBILE =
    "flex shrink-0 items-center gap-0.5 !w-auto !max-w-full !border-0 !bg-transparent !shadow-none !rounded-none px-0.5 text-[10px] leading-[13px] text-black";

const LIVE_INFO_ITEM_DESKTOP =
    "flex shrink-0 items-center justify-start gap-1 !w-auto !max-w-full !border-0 !bg-transparent !shadow-none !rounded-none text-xs leading-4 text-black";

export default function ViuGoOddsMatchRowMainContent({
    match,
    isLive,
    formattedDate,
    formattedGio,
    displayTimeMobile,
    currentHomeScore,
    currentAwayScore,
    currentHomeCorner,
    currentAwayCorner,
    homeYellow,
    awayYellow,
    homeRed,
    awayRed,
    mobileOddsSlide,
    setMobileOddsSlide,
    ftOddsColumnProps,
    htOddsColumnProps,
    liveScore,
    statusNum,
}) {
    const touchRef = useRef({
        startX: 0,
        startY: 0,
        swiped: false,
    });

    const onCarouselTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        touchRef.current.startX = t.clientX;
        touchRef.current.startY = t.clientY;
        touchRef.current.swiped = false;
    };

    const onCarouselTouchMove = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        const dx = t.clientX - touchRef.current.startX;
        const dy = t.clientY - touchRef.current.startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            // stop the page from horizontal scrolling while swiping the odds
            e.preventDefault?.();
        }
    };

    const onCarouselTouchEnd = (e) => {
        if (touchRef.current.swiped) return;
        const t = e.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - touchRef.current.startX;
        const dy = t.clientY - touchRef.current.startY;

        if (Math.abs(dx) <= Math.abs(dy)) return;

        const SWIPE_THRESHOLD = 40;
        if (dx <= -SWIPE_THRESHOLD) {
            touchRef.current.swiped = true;
            setMobileOddsSlide(1);
        } else if (dx >= SWIPE_THRESHOLD) {
            touchRef.current.swiped = true;
            setMobileOddsSlide(0);
        }
    };

    return (
        <>
            <div className="flex w-full [border-bottom:1px_solid_var(--theme-border)] font-bold md:hidden">
                <div className="flex w-[40%] shrink-0 flex-col border-r border-t-0 border-l-0 border-solid border-[var(--theme-border)] p-[5px] text-left">
                    <div className="flex flex-col py-3 md:py-0 gap-1 md:gap-3 ">
                        <div className="cursor-pointer select-none text-[10px] text-black hover:text-[var(--theme-highlight)]">
                            <div className="flex items-center justify-between gap-1">
                                <div className="flex min-w-0 flex-1 items-center gap-[5px]">
                                    <img
                                        className="h-[15px] w-[15px] shrink-0 opacity-100 transition-opacity duration-300"
                                        src={match.homeTeam?.logo || match.homeLogo}
                                        alt={match.homeName}
                                        width="15"
                                        height="15"
                                        loading="lazy"
                                    />
                                    <span className="line-clamp-1 min-w-0 flex-1 overflow-hidden text-ellipsis">
                                        {match.homeName}
                                    </span>
                                </div>
                                <div className="shrink-0 pl-1 text-right font-normal tabular-nums">
                                    {currentHomeScore ?? 0}
                                </div>
                            </div>
                        </div>
                        <div className="cursor-pointer select-none text-[10px] text-black hover:text-[var(--theme-highlight)]">
                            <div className="flex items-center justify-between gap-1">
                                <div className="flex min-w-0 flex-1 items-center gap-[5px]">
                                    <img
                                        className="h-[15px] w-[15px] shrink-0 opacity-100 transition-opacity duration-300"
                                        src={match.awayTeam?.logo || match.awayLogo}
                                        alt={match.awayName}
                                        width="15"
                                        height="15"
                                        loading="lazy"
                                    />
                                    <span className="line-clamp-1 min-w-0 flex-1 overflow-hidden text-ellipsis">
                                        {match.awayName}
                                    </span>
                                </div>
                                <div className="shrink-0 pl-1 text-right font-normal tabular-nums">
                                    {currentAwayScore ?? 0}
                                </div>
                            </div>
                        </div>

                        {isLive ? (
                            displayTimeMobile?.type === "var" ? (
                                <div className="kbv-text-result-indigo-slate mb-1 flex flex-row items-center justify-start gap-1 overflow-visible text-ellipsis text-[10px] font-bold leading-[13px]">
                                    <span className="shrink-0">
                                        {displayTimeMobile?.content}
                                    </span>
                                </div>
                            ) : (
                                <div
                                    className="kbv-text-result-indigo-slate mb-1 flex flex-row items-center justify-start gap-1 overflow-visible text-ellipsis text-[10px] font-bold leading-[13px]"
                                    aria-live="polite"
                                >
                                    <img
                                        src={viugoLiveWifi16}
                                        alt=""
                                        width={16}
                                        height={16}
                                        className="shrink-0"
                                        aria-hidden
                                    />
                                    <div className="shrink-0">
                                        {displayTimeMobile?.halfLabel || "Live"}
                                    </div>
                                    <div className="kbv-divider-text-indigo-slate shrink-0">|</div>
                                    <div className="shrink-0 whitespace-nowrap">
                                        <span className="tabular-nums">
                                            {displayTimeMobile?.content ?? ""}
                                        </span>
                                        <span className="viugo-odds-match-time__live-minus inline-block text-[10px] leading-[13px] !left-0 ml-0.5">
                                            &apos;
                                        </span>
                                    </div>
                                </div>
                            )
                        ) : (
                            <div className="mb-1 flex flex-row flex-wrap items-center justify-start gap-1 overflow-visible text-[10px] font-bold leading-[13px]">
                                <span className="kbv-text-result-indigo-slate shrink-0">
                                    {formattedDate}
                                </span>
                                <span className="shrink-0 text-[var(--text-half-score)]">
                                    {formattedGio}
                                </span>
                            </div>
                        )}

                        {isLive && (
                            <div className="md:mt-1 flex w-auto min-w-0 max-w-full flex-nowrap items-center gap-1 overflow-x-auto rounded border border-solid border-[var(--theme-live-chip-bg,var(--theme-live-chip-bg))] bg-[var(--theme-live-chip-bg,var(--theme-live-chip-bg))] px-1 py-0.5 font-normal not-italic text-black [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                <div className={LIVE_INFO_ITEM_MOBILE}>
                                    <img
                                        src={viugoLiveStatH}
                                        alt=""
                                        width={14}
                                        height={14}
                                        className="!h-3.5 !w-3.5 shrink-0"
                                        aria-hidden
                                    />
                                    <span>{`${currentHomeScore ?? 0}-${currentAwayScore ?? 0}`}</span>
                                </div>
                                <div className={LIVE_INFO_ITEM_MOBILE}>
                                    <img
                                        src={viugoLiveCornerMobile}
                                        alt=""
                                        width={14}
                                        height={14}
                                        className="!h-3.5 !w-3.5 shrink-0"
                                        aria-hidden
                                    />
                                    <span>{`${currentHomeCorner ?? 0}-${currentAwayCorner ?? 0}`}</span>
                                </div>
                                <div className={LIVE_INFO_ITEM_MOBILE} title="Thẻ vàng">
                                    <img
                                        src={viugoLiveCardYellow}
                                        alt=""
                                        width={14}
                                        height={14}
                                        className="!h-3.5 !w-3.5 shrink-0"
                                        aria-hidden
                                    />
                                    <span>{`${homeYellow ?? 0}-${awayYellow ?? 0}`}</span>
                                </div>
                                <div className={LIVE_INFO_ITEM_MOBILE} title="Thẻ đỏ">
                                    <img
                                        src={viugoLiveCardRed}
                                        alt=""
                                        width={14}
                                        height={14}
                                        className="!h-3.5 !w-3.5 shrink-0"
                                        aria-hidden
                                    />
                                    <span>{`${homeRed ?? 0}-${awayRed ?? 0}`}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div
                    className="relative flex min-w-0 flex-1 flex-col self-stretch overflow-x-hidden [border-bottom:1px_solid_var(--theme-border)]"
                    onTouchStart={onCarouselTouchStart}
                    onTouchMove={onCarouselTouchMove}
                    onTouchEnd={onCarouselTouchEnd}
                    style={{ touchAction: "pan-y" }}
                >
                    <div
                        className="grid w-[200%] shrink-0 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] transition-transform duration-300 ease-out motion-reduce:transition-none"
                        style={{
                            transform: `translateX(-${mobileOddsSlide * 50}%)`,
                        }}
                    >
                        <div className="min-w-0">
                            <ViuGoMobileOddsSlide
                                {...ftOddsColumnProps}
                                isHalf={false}
                            />
                        </div>
                        <div className="min-w-0">
                            <ViuGoMobileOddsSlide
                                {...htOddsColumnProps}
                                isHalf={true}
                            />
                        </div>
                    </div>
                    <div className="mt-auto flex items-center justify-center py-2">
                        <button
                            type="button"
                            aria-label="Kèo cả trận"
                            aria-current={mobileOddsSlide === 0}
                            onClick={() => setMobileOddsSlide(0)}
                            className={`mx-[5px] h-[5px] w-[5px] shrink-0 cursor-pointer rounded-full border-0 p-0 ${
                                mobileOddsSlide === 0
                                    ? "kbv-bg-dot-active-indigo-slate"
                                    : "kbv-bg-dot-inactive-indigo-slate"
                            }`}
                        />
                        <button
                            type="button"
                            aria-label="Kèo hiệp 1"
                            aria-current={mobileOddsSlide === 1}
                            onClick={() => setMobileOddsSlide(1)}
                            className={`mx-[5px] h-[5px] w-[5px] shrink-0 cursor-pointer rounded-full border-0 p-0 ${
                                mobileOddsSlide === 1
                                    ? "kbv-bg-dot-active-indigo-slate"
                                    : "kbv-bg-dot-inactive-indigo-slate"
                            }`}
                        />
                    </div>
                </div>
            </div>

            <div className="hidden w-full text-center font-bold md:flex">
                <ViuGoOddsMatchTime
                    match={match}
                    isLive={isLive}
                    formattedDate={formattedDate}
                    formattedGio={formattedGio}
                    liveScore={liveScore}
                    statusNum={statusNum}
                />

                <div className="w-[26%] [border-bottom:1px_solid_var(--theme-border)] [border-right:1px_solid_var(--theme-border)] py-2 text-left font-bold">
                    <div className="cursor-pointer select-none pl-2.5 text-[13px] text-black hover:text-[var(--theme-highlight)]">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center justify-start gap-[5px]">
                                <img
                                    className="opacity-100 transition-opacity duration-300"
                                    src={match.homeTeam?.logo || match.homeLogo}
                                    alt={match.homeName}
                                    width="24"
                                    height="24"
                                    loading="lazy"
                                />
                                <span className="line-clamp-1 block w-full overflow-hidden text-ellipsis">
                                    {match.homeName}
                                </span>
                            </div>
                            <div className="pr-1.5 font-normal">{currentHomeScore ?? 0}</div>
                        </div>
                    </div>

                    <div className="cursor-pointer select-none pl-2.5 text-[13px] text-black hover:text-[var(--theme-highlight)]">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center justify-start gap-[5px]">
                                <img
                                    className="opacity-100 transition-opacity duration-300"
                                    src={match.awayTeam?.logo || match.awayLogo}
                                    alt={match.awayName}
                                    width="24"
                                    height="24"
                                    loading="lazy"
                                />
                                <span className="line-clamp-1 block w-full overflow-hidden text-ellipsis">
                                    {match.awayName}
                                </span>
                            </div>
                            <div className="pr-1.5 font-normal">{currentAwayScore ?? 0}</div>
                        </div>
                    </div>

                    {isLive && (
                        <div className="min-w-0 pl-2.5 pr-1">
                            <div className="mt-2 flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 overflow-x-auto rounded border border-solid border-[var(--theme-live-chip-bg,var(--theme-live-chip-bg))] bg-[var(--theme-live-chip-bg,var(--theme-live-chip-bg))] px-2 py-1 font-normal not-italic [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                <div className={LIVE_INFO_ITEM_DESKTOP}>
                                    <img
                                        src={viugoLiveStatH}
                                        alt=""
                                        width={15}
                                        height={9}
                                        className="shrink-0"
                                    />
                                    <div>{`${currentHomeScore ?? 0}-${currentAwayScore ?? 0}`}</div>
                                </div>
                                <div className={LIVE_INFO_ITEM_DESKTOP}>
                                    <img
                                        src={viugoLiveCornerDesktop}
                                        alt=""
                                        width={18}
                                        height={18}
                                        className="shrink-0"
                                    />
                                    <div>{`${currentHomeCorner ?? 0}-${currentAwayCorner ?? 0}`}</div>
                                </div>
                                <div className={LIVE_INFO_ITEM_DESKTOP} title="Thẻ vàng">
                                    <img
                                        src={viugoLiveCardYellow}
                                        alt=""
                                        width={18}
                                        height={18}
                                        className="shrink-0"
                                        aria-hidden
                                    />
                                    <div>{`${homeYellow ?? 0}-${awayYellow ?? 0}`}</div>
                                </div>
                                <div className={LIVE_INFO_ITEM_DESKTOP} title="Thẻ đỏ">
                                    <img
                                        src={viugoLiveCardRed}
                                        alt=""
                                        width={18}
                                        height={18}
                                        className="shrink-0"
                                        aria-hidden
                                    />
                                    <div>{`${homeRed ?? 0}-${awayRed ?? 0}`}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <ViuGoOddsColumn {...ftOddsColumnProps} />
                <ViuGoOddsColumn {...htOddsColumnProps} />
            </div>
        </>
    );
}
